<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "vue";
// import { watch } from '@vue/composition-api'
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () => import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
	components: {
		// Layouts
		LayoutHorizontal,
		LayoutVertical,
		LayoutFull,

		ScrollToTop,
	},
	// ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
	// Currently, router.currentRoute is not reactive and doesn't trigger any change
	computed: {
		layout() {
			if (this.$route.meta.layout === "full") return "layout-full";
			return `layout-${this.contentLayoutType}`;
		},
		contentLayoutType() {
			return this.$store.state.appConfig.layout.type;
		},
	},
	beforeCreate() {
		// Set colors in theme
		const colors = [
			"primary",
			"secondary",
			"success",
			"info",
			"warning",
			"danger",
			"light",
			"dark",
		];

		// eslint-disable-next-line no-plusplus
		for (let i = 0, len = colors.length; i < len; i++) {
			$themeColors[colors[i]] = useCssVar(
				`--${colors[i]}`,
				document.documentElement
			).value.trim();
		}

		// Set Theme Breakpoints
		const breakpoints = ["xs", "sm", "md", "lg", "xl"];

		// eslint-disable-next-line no-plusplus
		for (let i = 0, len = breakpoints.length; i < len; i++) {
			$themeBreakpoints[breakpoints[i]] = Number(
				useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(
					0,
					-2
				)
			);
		}

		// Set RTL
		const { isRTL } = $themeConfig.layout;
		document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
	},
	setup() {
		const { skin, skinClasses } = useAppConfig();
		// const { enableScrollToTop } = $themeConfig.layout

		// If skin is dark when initialized => Add class to body
		if (skin.value === "dark") document.body.classList.add("dark-layout");

		// Provide toast for Composition API usage
		// This for those apps/components which uses composition API
		// Demos will still use Options API for ease
		// provideToast({
		// 	hideProgressBar: true,
		// 	closeOnClick: false,
		// 	closeButton: false,
		// 	icon: false,
		// 	timeout: 5000,
		// 	transition: "Vue-Toastification__fade",
		// });

		// Set Window Width in store
		store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
		const { width: windowWidth } = useWindowSize();
		watch(windowWidth, (val) => {
			store.commit("app/UPDATE_WINDOW_WIDTH", val);
		});

		return {
			skinClasses,
		};
	},
	mounted() {
		// add clarity
		// if (!document.location.href.match(/sharjmaster\.com/)) return
		// (function(c,l,a,r,i,t,y){
		// 	c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
		// 	t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
		// 	y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
		// })(window, document, "clarity", "script", "i10na7y3pe")
	},
};
</script>

<style>

@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 10;
	src: url("fonts/woff2/DanaFaNum-Hairline.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Hairline.woff")
			format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 100;
	src: url("fonts/woff2/DanaFaNum-Thin.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Thin.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 200;
	src: url("fonts/woff2/DanaFaNum-UltraLight.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-UltraLight.woff")
			format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 300;
	src: url("fonts/woff2/DanaFaNum-Light.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Light.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 500;
	src: url("fonts/woff2/DanaFaNum-Medium.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Medium.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 600;
	src: url("fonts/woff2/DanaFaNum-DemiBold.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-DemiBold.woff")
			format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 800;
	src: url("fonts/woff2/DanaFaNum-ExtraBold.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-ExtraBold.woff")
			format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: 900;
	src: url("fonts/woff2/DanaFaNum-Black.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Black.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: bold;
	src: url("fonts/woff2/DanaFaNum-Bold.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Bold.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}
@font-face {
	font-family: dana;
	font-style: normal;
	font-weight: normal;
	src: url("fonts/woff2/DanaFaNum-Regular.woff2") format("woff2"),
		/* FF39+,Chrome36+, Opera24+*/ url("fonts/woff/DanaFaNum-Regular.woff") format("woff"); /* FF3.6+, IE9, Chrome6+, Saf5.1+*/
}

* {
	font-family: dana;
}
html[dir] .navbar-sticky .app-content{
  padding: 79px 15px 15px 15px !important;
}
/*html[dir=rtl] .content{
  margin: 0 !important;
}*/
.content{
  max-width: 35rem !important;
  margin: 0 auto !important;
}
.top-menubar, .bottom-navbar{
  max-width: 35rem !important;
  margin: 0 auto !important;
  right:0;
  left: 0;
}

.Vue-Toastification__toast{
  padding-top: 18px;
  min-height: 64px;
  box-shadow: 2px 2px 8px rgba(0,0,0,0.2);
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}

</style>
